import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowLeft } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
};

const expertisesList = [
  { id: 'diabete', name: 'Diabète', icon: '🩸', color: 'from-blue-500 to-indigo-600', bgColor: 'bg-blue-50' },
  { id: 'hta', name: 'Hypertension (HTA)', icon: '❤️', color: 'from-red-500 to-rose-600', bgColor: 'bg-red-50' },
  { id: 'palu', name: 'Paludisme', icon: '🦟', color: 'from-green-500 to-emerald-600', bgColor: 'bg-green-50' },
  { id: 'grippe', name: 'États grippaux', icon: '🤒', color: 'from-amber-500 to-orange-600', bgColor: 'bg-amber-50' },
  { id: 'digestion', name: 'Troubles digestifs', icon: '🫃', color: 'from-orange-500 to-amber-600', bgColor: 'bg-orange-50' },
  { id: 'acne', name: 'Acné & Peau grasse', icon: '✨', color: 'from-pink-500 to-rose-600', bgColor: 'bg-pink-50' },
  { id: 'demangeaisons', name: 'Démangeaisons', icon: '🩹', color: 'from-purple-500 to-violet-600', bgColor: 'bg-purple-50' },
  { id: 'sommeil', name: 'Troubles du sommeil', icon: '😴', color: 'from-indigo-500 to-blue-600', bgColor: 'bg-indigo-50' },
  { id: 'migraines', name: 'Migraines', icon: '🤕', color: 'from-slate-500 to-gray-600', bgColor: 'bg-slate-50' },
  { id: 'peau-seche', name: 'Peau sèche', icon: '💧', color: 'from-cyan-500 to-blue-600', bgColor: 'bg-cyan-50' },
  { id: 'peau-grasse', name: 'Peau grasse', icon: '✨', color: 'from-lime-500 to-green-600', bgColor: 'bg-lime-50' },
  { id: 'taches-pigmentaires', name: 'Taches pigmentaires', icon: '🎨', color: 'from-amber-500 to-yellow-600', bgColor: 'bg-amber-50' },
  { id: 'fatigue', name: 'Fatigue', icon: '😫', color: 'from-gray-500 to-slate-600', bgColor: 'bg-gray-50' },
  { id: 'douleurs-articulaires', name: 'Douleurs articulaires', icon: '🦴', color: 'from-stone-500 to-neutral-600', bgColor: 'bg-stone-50' },
  { id: 'carence-vitamines', name: 'Carence en vitamines', icon: '💊', color: 'from-emerald-500 to-teal-600', bgColor: 'bg-emerald-50' },
];

const PharmacyExpertisesPage = ({ onBack, onNavigate }) => {
  const logoUrl = 'https://customer-assets.emergentagent.com/job_skincare-check-1/artifacts/6cemh04i_LOGO%20DEF.jpg';

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-xl border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              className="text-gray-600 hover:text-[#2E7D32] hover:bg-[#E8F5E9]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="h-9 w-auto object-contain cursor-pointer"
              onClick={() => onNavigate('home')}
            />
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="w-16 h-16 bg-[#E8F5E9] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg 
              viewBox="0 0 24 24" 
              className="w-8 h-8 text-[#2E7D32]"
              fill="currentColor"
            >
              <path d="M12 2C11.5 2 11 2.19 10.59 2.59L7.29 5.88C6.5 6.67 6 7.74 6 8.88C6 11.21 7.79 13 10.12 13H11V21C11 21.55 11.45 22 12 22S13 21.55 13 21V13H13.88C16.21 13 18 11.21 18 8.88C18 7.74 17.5 6.67 16.71 5.88L13.41 2.59C13 2.19 12.5 2 12 2M12 4.41L14.59 7H13V11H11V7H9.41L12 4.41M10.12 9C9.5 9 9 9.5 9 10.12C9 10.5 9 10.88 9 11H10.12C10.5 11 10.88 11 11 11V9H10.12M13 9V11C13.12 11 13.5 11 13.88 11H15C15 10.88 15 10.5 15 10.12C15 9.5 14.5 9 13.88 9H13Z"/>
            </svg>
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Nos domaines d'expertises
          </h1>
          <p className="text-gray-600 max-w-lg mx-auto">
            Retrouvez des informations, conseils et produits recommandés pour chaque pathologie
          </p>
        </motion.div>

        {/* Grid of Expertises */}
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-3 gap-4"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {expertisesList.map((expertise) => (
            <motion.div key={expertise.id} variants={fadeInUp}>
              <Card
                className={`${expertise.bgColor} border-0 cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all duration-300 rounded-2xl overflow-hidden group`}
                onClick={() => onNavigate('pathology', { id: expertise.id })}
                data-testid={`expertise-${expertise.id}`}
              >
                <CardContent className="p-5 text-center">
                  <span className="text-4xl mb-3 block group-hover:scale-110 transition-transform">
                    {expertise.icon}
                  </span>
                  <h3 className="font-semibold text-gray-800 text-sm leading-tight">
                    {expertise.name}
                  </h3>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Info Banner */}
        <motion.div 
          className="mt-8 p-4 bg-[#E8F5E9] rounded-xl border border-[#2E7D32]/20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <p className="text-sm text-gray-700 text-center">
            <span className="font-semibold text-[#2E7D32]">Conseil :</span> Ces informations sont données à titre indicatif. 
            Pour tout problème de santé, consultez un professionnel de santé ou passez nous voir en pharmacie.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PharmacyExpertisesPage;
